/**
 * Maintenance window switch.
 *
 * While the window is open, every request except the landing page, the health
 * probes, public content and its read APIs, the status endpoint, and static assets is turned away:
 *   - navigations get a temporary redirect to `/`, where the banner explains why
 *   - data / API / server-function calls get a 503 they can actually parse,
 *     rather than an HTML page that blows up in `response.json()`
 *
 * The flag is read from `process.env` at runtime, and ONLY on the server. The
 * browser never reads it: it asks `MAINTENANCE_STATUS_PATH` instead (see
 * `src/features/maintenance/queries.ts`). Reading it in the browser means
 * reading a value Vite baked in at build time, which lets an open tab disagree
 * with the server about whether the window is open.
 *
 * Unset means ON. Set `VITE_PUBLIC_MAINTENANCE_MODE=false` and restart to close
 * the window; no rebuild needed. The `VITE_PUBLIC_` prefix is historical and
 * kept so the value already configured in deploy environments keeps working.
 */

import { isContentRequest } from "@/content-site/lib/paths";

export const MAINTENANCE_MESSAGE =
  "We are currently in the middle of a maintenance window. We apologize for the inconvenience.";

/** Where turned-away navigations land. */
export const MAINTENANCE_LANDING_PATH = "/";

/** Answers "is the window open?" for the browser. Must stay exempt. */
export const MAINTENANCE_STATUS_PATH = "/api/maintenance";

/** Advertised to clients and crawlers on every blocked response, in seconds. */
export const MAINTENANCE_RETRY_AFTER_SECONDS = 3600;

export const MAINTENANCE_FLAG = "VITE_PUBLIC_MAINTENANCE_MODE";

/** With the flag unset, the window stays open. Flip it to one of these to leave. */
const DISABLED_VALUES = new Set(["false", "0", "off", "no"]);

/**
 * Paths that must keep working while the window is open.
 *
 * The health probes matter most: platform health checks and uptime monitors hit
 * them, and redirecting those would fail deploys and page on-call for an outage
 * we caused on purpose. The status endpoint is how the browser learns the
 * window is open, so blocking it would be circular.
 */
const EXEMPT_EXACT_PATHS = new Set([
  MAINTENANCE_LANDING_PATH,
  MAINTENANCE_STATUS_PATH,
  "/api/healthz",
  "/api/readyz",
]);

const EXEMPT_PATH_PREFIXES = [
  "/assets/", // hashed JS/CSS — without these the landing page never hydrates
  "/_build/",
  "/og/", // social preview images
  "/.well-known/",
  "/@vite", // dev server internals
  "/@fs/",
  "/@id/",
  "/@react-refresh",
  "/src/",
  "/node_modules/",
];

/**
 * Anything with a static-asset extension is served, not redirected. Belt and
 * braces: in production Nitro serves `public/` before this code runs, but that
 * ordering is not something worth betting the landing page's stylesheet on.
 */
const STATIC_FILE_EXTENSION =
  /\.(?:avif|css|eot|gif|ico|jpe?g|js|json|map|mjs|mp4|otf|pdf|png|svg|ttf|txt|wasm|webm|webp|woff2?|xml)$/i;

export function parseMaintenanceFlag(raw: string | undefined): boolean {
  const value = raw?.trim().toLowerCase();
  if (value === undefined || value === "") {
    return true;
  }

  return !DISABLED_VALUES.has(value);
}

/**
 * Server-only. In the browser `process.env` is absent or shimmed empty, which
 * reads as "unset" and therefore ON, so browser code must go through the status
 * query instead of calling this.
 */
export function isMaintenanceModeEnabled(): boolean {
  const raw = typeof process === "undefined" ? undefined : process.env?.[MAINTENANCE_FLAG];
  return parseMaintenanceFlag(raw);
}

function normalizePath(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }

  return pathname;
}

export function isMaintenanceExemptPath(pathname: string): boolean {
  const normalized = normalizePath(pathname);
  if (isContentRequest(normalized)) return true;

  if (EXEMPT_EXACT_PATHS.has(normalized)) {
    return true;
  }

  if (EXEMPT_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix))) {
    return true;
  }

  return STATIC_FILE_EXTENSION.test(normalized);
}

/** True when the caller is a browser asking for a page, not code asking for data. */
function isDocumentRequest(request: Request): boolean {
  if (request.headers.get("sec-fetch-mode") === "navigate") {
    return true;
  }

  return (request.headers.get("accept") ?? "").includes("text/html");
}

/**
 * The response that should replace this request, or `null` to let it through.
 */
export function buildMaintenanceResponse(request: Request): Response | null {
  if (!isMaintenanceModeEnabled()) {
    return null;
  }

  const { pathname } = new URL(request.url);
  if (isMaintenanceExemptPath(pathname)) {
    return null;
  }

  const headers = new Headers({
    // Never let a CDN or browser hold on to this past the window.
    "cache-control": "no-store, must-revalidate",
    "retry-after": String(MAINTENANCE_RETRY_AFTER_SECONDS),
  });

  if (isDocumentRequest(request)) {
    // 302, never 301: a permanent redirect would be cached by browsers long
    // after the window closes, and there is no way to recall it.
    headers.set("location", MAINTENANCE_LANDING_PATH);
    return new Response(null, { status: 302, headers });
  }

  headers.set("content-type", "application/json; charset=utf-8");
  return new Response(
    JSON.stringify({ error: "service_unavailable", message: MAINTENANCE_MESSAGE }),
    { status: 503, headers },
  );
}
